/**
 * Görünüm (kişisel arayüz tercihleri) — TEK KAYNAK (devam/gorunum/SOZLESME.md §2).
 *
 * Saklama:
 *  - localStorage `mino.gorunum.<userId>` : kullanıcının tercihi (JSON, {@link GorunumTercihi})
 *  - localStorage `mino.gorunum.son`      : cihazdaki son tercih (kullanıcı henüz bilinmezken, ör. giriş ekranında)
 *  - sunucu (hesapla birlikte, her bilgisayarda): useGorunumSenkron.ts — localStorage onun önbelleğidir
 * Kullanıcıya özel kayıt yoksa (ilk kez) eski Pusula anahtarı `mino.menu.dar.<userId>` "1" ise menü "dar" başlar (tek
 * seferlik göç; eski anahtar bir daha yazılmaz).
 *
 * <html> öznitelikleri (çözümlenmiş; CSS YALNIZ bunlara bakar):
 *   data-menu="genis|dar|gizli|tam-ekran"  data-tema="acik|koyu"  data-yogunluk="siki|ferah"  data-hareket="az|normal"
 *
 * DİKKAT: public/index.html <head>'indeki satır içi betik aynı okumayı ve çözümlemeyi ilk boyamadan önce yapar
 * (gorunumOku + htmlUygula). Buradaki mantık (anahtarlar, geçerli değerler, varsayılanlar, OZELLIKLER) değişirse
 * oradaki betik de AYNI biçimde değiştirilmeli.
 */

export type MenuDuzeni = "genis" | "dar" | "gizli" | "tamEkran";   // tamEkran Aşama 2
export type TemaSecimi = "acik" | "koyu" | "sistem";               // koyu/sistem Aşama 4 bitene kadar ekranda yok
export type TabloYogunlugu = "siki" | "ferah";                     // siki = bugünkü
export type HareketSecimi = "sistem" | "az" | "normal";

export interface GorunumTercihi {
    surum: 1;
    /** varsayılan "genis"; ilk okumada eski `mino.menu.dar.<id>` true ise "dar" */
    menuDuzeni: MenuDuzeni;
    /** varsayılan "acik" */
    tema: TemaSecimi;
    /** varsayılan "siki" */
    tabloYogunlugu: TabloYogunlugu;
    /** varsayılan "sistem" */
    hareket: HareketSecimi;
    /** varsayılan true (bugünkü davranış) */
    sekmeleriGeriYukle: boolean;
}

export const VARSAYILAN_GORUNUM: GorunumTercihi = Object.freeze({
    surum: 1,
    menuDuzeni: "genis",
    tema: "acik",
    tabloYogunlugu: "siki",
    hareket: "sistem",
    sekmeleriGeriYukle: true,
}) as GorunumTercihi;

/** aşamalar bitince true (index.html'deki betikte de aynı bayraklar var) */
export const OZELLIKLER: { readonly TAM_EKRAN_MENU: boolean, readonly KOYU_TEMA: boolean } =
    Object.freeze({TAM_EKRAN_MENU: false, KOYU_TEMA: false});

const ONEK = "mino.gorunum.";
const SON_ANAHTAR = "mino.gorunum.son";
const ESKI_DAR_ONEK = "mino.menu.dar.";

const MENU_DUZENLERI: MenuDuzeni[] = ["genis", "dar", "gizli", "tamEkran"];
const TEMALAR: TemaSecimi[] = ["acik", "koyu", "sistem"];
const YOGUNLUKLAR: TabloYogunlugu[] = ["siki", "ferah"];
const HAREKETLER: HareketSecimi[] = ["sistem", "az", "normal"];

/** kullanıcı id'si hazır mı (0 / boş değil): hazır değilse kullanıcıya özel anahtar okunmaz, yazılmaz */
const kullaniciHazir = (id: unknown): id is number =>
    id !== undefined && id !== null && id !== 0 && id !== "";

const oku = (anahtar: string): string | null => {
    try {
        return window.localStorage.getItem(anahtar);
    } catch (e) {
        return null;
    }
};

const yaz = (anahtar: string, deger: string) => {
    try {
        window.localStorage.setItem(anahtar, deger);
    } catch (e) {
        /* kota dolu ya da özel mod: sessizce vazgeç */
    }
};

/** localStorage 'user' (girişte authSlice yazar) → userId; yoksa / bozuksa undefined */
export const gecerliKullaniciId = (): number | undefined => {
    try {
        const ham = oku("user");
        const id = ham ? JSON.parse(ham)?.userId : undefined;
        return kullaniciHazir(id) ? id : undefined;
    } catch (e) {
        return undefined;
    }
};

const secenek = <T extends string>(deger: unknown, gecerli: T[], varsayilan: T): T =>
    gecerli.includes(deger as T) ? deger as T : varsayilan;

/**
 * Ham değerden (nesne ya da JSON metni) geçerli tercih: bilinmeyen alan / değer atılır, eksik alan varsayılanla dolar.
 * Alan sırası sabit (JSON karşılaştırması için).
 */
export const gorunumDogrula = (ham: unknown): GorunumTercihi => {
    let kaynak: any = ham;
    if (typeof ham === "string") {
        try {
            kaynak = JSON.parse(ham);
        } catch (e) {
            kaynak = undefined;
        }
    }
    if (!kaynak || typeof kaynak !== "object" || Array.isArray(kaynak)) {
        kaynak = {};
    }
    const v = VARSAYILAN_GORUNUM;
    return {
        surum: 1,
        menuDuzeni: secenek(kaynak.menuDuzeni, MENU_DUZENLERI, v.menuDuzeni),
        tema: secenek(kaynak.tema, TEMALAR, v.tema),
        tabloYogunlugu: secenek(kaynak.tabloYogunlugu, YOGUNLUKLAR, v.tabloYogunlugu),
        hareket: secenek(kaynak.hareket, HAREKETLER, v.hareket),
        sekmeleriGeriYukle: typeof kaynak.sekmeleriGeriYukle === "boolean" ? kaynak.sekmeleriGeriYukle : v.sekmeleriGeriYukle,
    };
};

/** iki tercih aynı mı (alan alan) */
export const gorunumAyni = (a: GorunumTercihi, b: GorunumTercihi): boolean =>
    JSON.stringify(gorunumDogrula(a)) === JSON.stringify(gorunumDogrula(b));

/**
 * Kullanıcının saklanan tercihi: `mino.gorunum.<id>`; yoksa göç (eski `mino.menu.dar.<id>` "1" → "dar") ya da varsayılan.
 * Kullanıcı id'si yoksa yalnız cihaz kopyası `mino.gorunum.son`.
 */
export const gorunumOku = (kullaniciId?: number): GorunumTercihi => {
    if (!kullaniciHazir(kullaniciId)) {
        return gorunumDogrula(oku(SON_ANAHTAR));
    }
    const ham = oku(ONEK + kullaniciId);
    if (ham) {
        return gorunumDogrula(ham);
    }
    return oku(ESKI_DAR_ONEK + kullaniciId) === "1"
        ? gorunumDogrula({menuDuzeni: "dar"})
        : gorunumDogrula(undefined);
};

/** Tercihi tarayıcıya yazar: kullanıcıya özel anahtar (id hazırsa) + cihaz kopyası `mino.gorunum.son` */
export const gorunumYazYerel = (kullaniciId: number | undefined, t: GorunumTercihi): void => {
    const json = JSON.stringify(gorunumDogrula(t));
    if (kullaniciHazir(kullaniciId)) {
        yaz(ONEK + kullaniciId, json);
    }
    yaz(SON_ANAHTAR, json);
};

/** Tarayıcıdaki kullanıcıya özel anahtar (başka sekmedeki değişikliği izlemek için: `storage` olayı) */
export const gorunumAnahtari = (kullaniciId: number): string => ONEK + kullaniciId;

const medya = (sorgu: string): boolean => {
    try {
        return !!window.matchMedia?.(sorgu)?.matches;
    } catch (e) {
        return false;
    }
};

export const KOYU_SORGUSU = "(prefers-color-scheme: dark)";
export const HAREKET_AZ_SORGUSU = "(prefers-reduced-motion: reduce)";

/** etkin menü düzeni: açılmamış aşamanın düzeni (tamEkran) bugünkü düzene düşer */
export const etkinMenuDuzeni = (t: GorunumTercihi): MenuDuzeni =>
    t.menuDuzeni === "tamEkran" && !OZELLIKLER.TAM_EKRAN_MENU ? "genis" : t.menuDuzeni;

/** etkin tema: koyu tema açılmadıysa HER ZAMAN "acik"; "sistem" işletim sisteminin ayarından */
export const etkinTema = (t: GorunumTercihi): "acik" | "koyu" => {
    if (!OZELLIKLER.KOYU_TEMA) {
        return "acik";
    }
    if (t.tema === "sistem") {
        return medya(KOYU_SORGUSU) ? "koyu" : "acik";
    }
    return t.tema === "koyu" ? "koyu" : "acik";
};

/** etkin hareket: "sistem" işletim sisteminin "hareketi azalt" ayarından */
export const etkinHareket = (t: GorunumTercihi): "az" | "normal" => {
    if (t.hareket === "sistem") {
        return medya(HAREKET_AZ_SORGUSU) ? "az" : "normal";
    }
    return t.hareket === "az" ? "az" : "normal";
};

/** <html> öznitelikleri (çözümlenmiş değerler) */
export const htmlUygula = (t: GorunumTercihi): void => {
    try {
        const kok = document.documentElement;
        const menu = etkinMenuDuzeni(t);
        kok.setAttribute("data-menu", menu === "tamEkran" ? "tam-ekran" : menu);
        kok.setAttribute("data-tema", etkinTema(t));
        kok.setAttribute("data-yogunluk", t.tabloYogunlugu === "ferah" ? "ferah" : "siki");
        kok.setAttribute("data-hareket", etkinHareket(t));
    } catch (e) {
        /* belge yok (test ortamı) */
    }
};
